import React from 'react';
import { ApiClientService } from '../api-client.service';
import { useUserState } from '../../state/UserState';

/** Event names the backend sends. Must match `SseEventType` on the server. */
export type SseEventName =
  | 'report-generation-started'
  | 'report-generated'
  | 'settlement-import'
  | 'settlement-export'
  | 'branches-import'
  | 'received-quantities-import'
  | 'notification'
  | 'update-mpd-bdo';

type SseHandler<T = unknown> = (payload: T) => void;

/**
 * The subscribe endpoint sits behind both the servlet context path (`/api`) and PreBoot's own `/api` request mapping,
 * hence the doubled segment.
 */
const SUBSCRIBE_PATH = '/api/api/notifications/sse/subscribe';
const TICKET_PATH = '/api/notifications/sse/ticket';
const RECONNECT_DELAY_MS = 5000;
const MAX_RECONNECT_DELAY_MS = 60_000;

interface SseContextValue {
  subscribe: (eventName: SseEventName, handler: SseHandler) => () => void;
}

const SseContext = React.createContext<SseContextValue | null>(null);

/**
 * Owns the single EventSource the whole app shares.
 *
 * One connection, many event names — a browser allows only six concurrent HTTP/1.1 connections per origin, and an SSE
 * stream holds its one open for as long as the tab lives.
 *
 * Authentication: `EventSource` cannot send an `Authorization` header, so the connection presents a one-time ticket
 * fetched over the authenticated API. A fresh ticket is fetched for every (re)connect because the server spends it on
 * first use. `withCredentials` sends the `jwtFingerprint` cookie, which the server compares against the one present
 * when it issued the ticket.
 */
export const SseProvider = ({ children }: { children: React.ReactNode }) => {
  const { token, isPresent } = useUserState();
  const handlersRef = React.useRef(new Map<SseEventName, Set<SseHandler>>());
  const listenersRef = React.useRef(new Map<SseEventName, (event: MessageEvent) => void>());
  const eventSourceRef = React.useRef<EventSource | null>(null);

  const dispatcherFor = React.useCallback((eventName: SseEventName) => {
    let listener = listenersRef.current.get(eventName);
    if (!listener) {
      listener = (event: MessageEvent) => {
        const payload = event.data ? JSON.parse(event.data) : undefined;
        handlersRef.current.get(eventName)?.forEach((handler) => handler(payload));
      };
      listenersRef.current.set(eventName, listener);
    }
    return listener;
  }, []);

  const subscribe = React.useCallback(
    (eventName: SseEventName, handler: SseHandler) => {
      const handlers = handlersRef.current.get(eventName) ?? new Set<SseHandler>();
      handlers.add(handler);
      handlersRef.current.set(eventName, handlers);
      // A connection that already exists has no listener for an event name nobody had subscribed to yet.
      eventSourceRef.current?.addEventListener(eventName, dispatcherFor(eventName) as EventListener);

      return () => {
        handlers.delete(handler);
      };
    },
    [dispatcherFor],
  );

  React.useEffect(() => {
    if (!token || !isPresent) {
      return;
    }

    let cancelled = false;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let failures = 0;

    // Backs off instead of hammering: a tab left open on a dead session used to ask for a ticket every 5 seconds
    // forever, which shows up in the server log as a steady stream of failed authentications.
    const scheduleReconnect = () => {
      if (cancelled) {
        return;
      }
      const delay = Math.min(RECONNECT_DELAY_MS * 2 ** failures, MAX_RECONNECT_DELAY_MS);
      failures += 1;
      reconnectTimeout = setTimeout(connect, delay);
    };

    async function connect() {
      if (cancelled) {
        return;
      }
      const response = await ApiClientService.post<Record<string, never>, { ticket: string }>(TICKET_PATH, {});
      if (cancelled) {
        return;
      }
      // 401 means this session cannot subscribe at all. Retrying changes nothing; logging in again re-runs this effect
      // with a new token, which is the only thing that can help.
      if (response && 'httpStatus' in response && response.httpStatus === 401) {
        return;
      }
      if (!response || !('ticket' in response)) {
        scheduleReconnect();
        return;
      }

      const url = `${import.meta.env.VITE_API_URL}${SUBSCRIBE_PATH}?ticket=${encodeURIComponent(response.ticket)}`;
      const eventSource = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = eventSource;
      handlersRef.current.forEach((_, eventName) => eventSource.addEventListener(eventName, dispatcherFor(eventName) as EventListener));

      eventSource.onopen = () => {
        failures = 0;
      };

      eventSource.onerror = () => {
        eventSource.close();
        if (eventSourceRef.current === eventSource) {
          eventSourceRef.current = null;
        }
        scheduleReconnect();
      };
    }

    void connect();

    return () => {
      cancelled = true;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
  }, [token, isPresent, dispatcherFor]);

  const value = React.useMemo(() => ({ subscribe }), [subscribe]);
  return <SseContext.Provider value={value}>{children}</SseContext.Provider>;
};

/**
 * Runs `handler` whenever the server sends `eventName`. The handler may change on every render — only the event name
 * decides the subscription.
 */
export const useSseEvent = <T = unknown,>(eventName: SseEventName, handler: SseHandler<T>): void => {
  const context = React.useContext(SseContext);
  const handlerRef = React.useRef(handler);

  React.useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  React.useEffect(() => {
    // No provider means SSE is not mounted for this part of the tree (public pages, tests) — nothing to subscribe to.
    return context?.subscribe(eventName, (payload) => handlerRef.current(payload as T));
  }, [context, eventName]);
};
